// src/pages/LandingPage.jsx
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { isAuthenticated } from '../utils/auth';
import SmartNavbar from '../components/SmartNavbar';
import PricingSection from '../components/PricingSection';
import { Zap, ShieldCheck, Smartphone, FileText, Code, Palette } from 'lucide-react';

export default function LandingPage() {
    const navigate = useNavigate();

    // Redirect logged-in users to dashboard
    useEffect(() => {
        if (isAuthenticated()) {
            navigate('/dashboard');
        }
    }, [navigate]);

    const handleGetStarted = () => {
        navigate('/register');
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <SmartNavbar />

            {/* Hero Section */}
            <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
                        Screenshot Any Website<br />
                        <span className="text-blue-600">in Seconds</span>
                    </h1>
                    <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                        Simple API for developers. One line of code. No complexity.
                        Capture screenshots, generate PDFs, and automate visual content.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                        <button
                            onClick={handleGetStarted}
                            className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
                        >
                            Start Free - 100 Requests/Month
                        </button>
                        <button
                            onClick={() => navigate('/docs')}
                            className="bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold border-2 border-gray-300 hover:border-gray-400 transition-colors"
                        >
                            View Documentation
                        </button>
                    </div>

                    {/* Trust Badge */}
                    <p className="text-sm text-gray-500">
                        No credit card required · Free tier forever · Lightning-fast responses
                    </p>

                    {/* Code Example */}
                    <div className="mt-16 max-w-3xl mx-auto">
                        <div className="bg-gray-900 rounded-lg p-6 text-left shadow-2xl">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-gray-400 text-sm font-mono">One API call</span>
                                <span className="text-green-400 text-xs">✓ Live API</span>
                            </div>
                            <pre className="text-gray-100 font-mono text-sm overflow-x-auto">
                                {`curl -X POST https://api.snaptura.online/screenshot \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://example.com",
    "fullPage": true
  }'`}
                            </pre>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Everything You Need
                        </h2>
                        <p className="text-xl text-gray-600">
                            Built for developers who value simplicity and speed
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Feature 1 */}
                        <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                                <Zap className="w-6 h-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                1-Second Response Time
                            </h3>
                            <p className="text-gray-600">
                                Lightning-fast screenshot generation. No waiting. Get your image URL instantly.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                                <ShieldCheck className="w-6 h-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                Auto-Blocks Ads & Cookies
                            </h3>
                            <p className="text-gray-600">
                                Clean screenshots without ads, cookie banners, or tracking scripts. Automatically.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                                <Smartphone className="w-6 h-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                Device Emulation
                            </h3>
                            <p className="text-gray-600">
                                iPhone, iPad, Android, Desktop. See how your site looks on any device instantly.
                            </p>
                        </div>

                        {/* Feature 4 */}
                        <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                                <FileText className="w-6 h-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                PDF Generation
                            </h3>
                            <p className="text-gray-600">
                                Generate PDFs from URLs or HTML. Perfect for reports, invoices, and documents.
                            </p>
                        </div>

                        {/* Feature 5 */}
                        <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                                <Code className="w-6 h-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                5-Line Integration
                            </h3>
                            <p className="text-gray-600">
                                Simple REST API. Works with any language. Copy, paste, done. No complex setup.
                            </p>
                        </div>

                        {/* Feature 6 */}
                        <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                                <Palette className="w-6 h-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                Custom CSS & JavaScript
                            </h3>
                            <p className="text-gray-600">
                                Inject custom styles or scripts. Hide elements. Modify pages before capture.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Cases Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Built For Your Use Case
                        </h2>
                        <p className="text-xl text-gray-600">
                            Whether you're building SaaS, monitoring sites, or automating reports
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="border-l-4 border-blue-600 pl-6">
                            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                                Social Media Cards
                            </h3>
                            <p className="text-gray-600 mb-4">
                                Generate Open Graph images dynamically. Perfect for blogs, portfolios, and content sites.
                            </p>
                            <code className="text-sm bg-gray-100 px-3 py-1 rounded">
                                og:image generation
                            </code>
                        </div>

                        <div className="border-l-4 border-blue-600 pl-6">
                            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                                Automated Monitoring
                            </h3>
                            <p className="text-gray-600 mb-4">
                                Track website changes visually. Get alerted when pages change. Perfect for QA and testing.
                            </p>
                            <code className="text-sm bg-gray-100 px-3 py-1 rounded">
                                visual regression testing
                            </code>
                        </div>

                        <div className="border-l-4 border-blue-600 pl-6">
                            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                                Client Reports
                            </h3>
                            <p className="text-gray-600 mb-4">
                                Generate PDF reports with screenshots. Automate agency workflows. Impress clients.
                            </p>
                            <code className="text-sm bg-gray-100 px-3 py-1 rounded">
                                automated reporting
                            </code>
                        </div>

                        <div className="border-l-4 border-blue-600 pl-6">
                            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                                Link Previews
                            </h3>
                            <p className="text-gray-600 mb-4">
                                Create Slack/Discord-style link previews. Show website thumbnails in your app.
                            </p>
                            <code className="text-sm bg-gray-100 px-3 py-1 rounded">
                                URL unfurling
                            </code>
                        </div>
                    </div>
                </div>
            </section>

            <PricingSection id="pricing" />

            {/* CTA Section */}
            <section className="py-20 bg-blue-600">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <h2 className="text-4xl font-bold text-white mb-6">
                        Ready to Start Capturing Screenshots?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8">
                        Join developers who trust Snaptura for their screenshot needs.
                        Start free, no credit card required.
                    </p>
                    <button
                        onClick={handleGetStarted}
                        className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                    >
                        Start Free - Get Your API Key
                    </button>
                    <p className="text-blue-100 mt-4 text-sm">
                        100 free requests every month · Forever
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-8">
                        <div>
                            <h3 className="text-white font-semibold mb-4">Snaptura</h3>
                            <p className="text-sm">
                                Simple screenshot API for developers. Fast, reliable, affordable.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-white font-semibold mb-4">Product</h4>
                            <ul className="space-y-2 text-sm">
                                <li><a href="#features" className="hover:text-white">Features</a></li>
                                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                                <li><a href="/docs" className="hover:text-white">Documentation</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-white font-semibold mb-4">Company</h4>
                            <ul className="space-y-2 text-sm">
                                <li><a href="mailto:support@snaptura.online" className="hover:text-white">Contact</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-white font-semibold mb-4">Legal</h4>
                            <ul className="space-y-2 text-sm">
                                <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
                                <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
                                <li><a href="/cookies" className="hover:text-white">Cookie Policy</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-center">
                        <p>&copy; 2025 Snaptura. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}